import { defineComponent as _defineComponent } from 'vue'
import { getCurrentInstance, ref } from 'vue'

import Pagination from '@/client/components/_atoms/pagination/pagination.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import Util from '@/client/core/util'
import { formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import type { TPrize } from '@/typings/api/instagram_instantwin_prizes.schema'

import { postInstagramInstantwinPrizeCopy } from '../api/postInstagramInstantwinPrizeCopy'
import { buildInstantwinPrizeKeywordText } from '../utils/prizeKeyword'
import InstagramDmTestDialog from './InstagramDmTestDialog/InstagramDmTestDialog.vue'
import InstagramInstantwinPrizeEditDialog from './InstagramInstantwinPrizeEditDialog.vue'
import InstagramScenarioEditorDialog from './InstagramScenarioEditorDialog/InstagramScenarioEditorDialog.vue'
import InstagramScenarioPreviewDialog from './InstagramScenarioPreviewDialog/InstagramScenarioPreviewDialog.vue'

interface Props {
  prizes: TPrize[]
  campaignId: number
  campaignStatus?: number
  hasRunningCampaignOnSameAccount?: boolean
  canEditCampaign?: boolean
  options: {
    total: number
    pagingNo: number
    limitCount: number
    sortBy: string
  }
  onChangePagingNo: (page: number) => void
  onChangeLimitCount: (limitCount: number) => void
  onChangeSortBy: (sortBy: string) => void
  onOpenApplicant: (prizeId: number) => void
  onRefresh: () => Promise<void> | void
}

type TableHeaderColumn = {
  label: string
  sortBy?: string
}

/**
 * ヘッダーをクリックするとソートする
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'InstagramInstantwinPrizeTable',
  props: {
    prizes: null,
    campaignId: null,
    campaignStatus: null,
    hasRunningCampaignOnSameAccount: { type: Boolean },
    canEditCampaign: { type: Boolean },
    options: null,
    onChangePagingNo: null,
    onChangeLimitCount: null,
    onChangeSortBy: null,
    onOpenApplicant: null,
    onRefresh: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  prizes: TPrize[]
  campaignId: number
  campaignStatus?: number
  hasRunningCampaignOnSameAccount?: boolean
  canEditCampaign?: boolean
  options: {
    total: number
    pagingNo: number
    limitCount: number
    sortBy: string
  }
  onChangePagingNo: (page: number) => void
  onChangeLimitCount: (limitCount: number) => void
  onChangeSortBy: (sortBy: string) => void
  onOpenApplicant: (prizeId: number) => void
  onRefresh: () => Promise<void> | void
};

// @ts-nocheck TODO: 既存の型エラーを解消したら外す

const { proxy } = getCurrentInstance() as {
  proxy: {
    $notify: (arg: { title: string; customClass: string; duration: number }) => void
  }
}

// モーダル管理用の状態
const isEditDialogVisible = ref(false)
const editingPrizeId = ref<number | null>(null)
const isScenarioEditorVisible = ref(false)
const editingScenarioPrizeId = ref<number | null>(null)
const isPretestDialogVisible = ref(false)
const activePretestPrizeId = ref<number | null>(null)
const isPreviewDialogVisible = ref(false)
const previewingPrizeId = ref<number | null>(null)
const copyingPrizeId = ref<number | null>(null)
function headerClick(column: TableHeaderColumn) {
  if (isOperationDisabled()) {
    return
  }

  switch (column.label) {
    case '応募グループ名':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|応募グループ名')
      break
    case '応募キーワード':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|応募キーワード')
      break
    case '応募者数':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|応募者数')
      break
    case '当選者数':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|当選者数')
      break
    default:
      break
  }

  if (!column.sortBy) {
    return
  }

  if (column.sortBy.indexOf('-') === 0) {
    if (props.options.sortBy === column.sortBy) {
      props.onChangeSortBy(column.sortBy.substring(1))
    } else {
      props.onChangeSortBy(column.sortBy)
    }
  } else {
    if (props.options.sortBy === column.sortBy) {
      props.onChangeSortBy('-' + column.sortBy)
    } else {
      props.onChangeSortBy(column.sortBy)
    }
  }
}

/**
 * ヘッダー描画
 */
function renderHeader(createElement: (...args: unknown[]) => unknown, { column }: { column: TableHeaderColumn }) {
  return Util.renderHeader(createElement, column.label, Util.getSortClass(props.options.sortBy, column.sortBy))
}

function getKeywordText(prizeEntryKeyword: string[] | null | undefined) {
  return buildInstantwinPrizeKeywordText({
    prizeEntryKeyword
  })
}

/**
 * 応募者一覧を開く
 */
function openCampaignApplicant(id: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|グループ名')
  props.onOpenApplicant(id)
}

/**
 * 編集ダイアログを開く
 */
function openEditDialog(prizeId: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|編集')
  editingPrizeId.value = prizeId
  isEditDialogVisible.value = true
}

/**
 * 新規作成ダイアログを開く
 */
function openCreateDialog() {
  editingPrizeId.value = null
  isEditDialogVisible.value = true
}

/**
 * 編集ダイアログを閉じる
 */
function closeEditDialog() {
  isEditDialogVisible.value = false
  editingPrizeId.value = null
}

/**
 * 保存成功時の処理
 */
function onSaved(mode: 'create' | 'edit') {
  closeEditDialog()
  if (mode === 'edit') {
    props.onRefresh()
  }
}

/**
 * シナリオエディタを開く
 */
function openScenarioEditor(prizeId: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|シナリオ編集')
  editingScenarioPrizeId.value = prizeId
  isScenarioEditorVisible.value = true
}

function openPretestDialog(prizeId: number) {
  if (hasCopyInProgress()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|テスト')
  activePretestPrizeId.value = prizeId
  isPretestDialogVisible.value = true
}

function isBeforeEventCampaign() {
  return props.campaignStatus === CAMPAIGN_STATUS.BEFORE_EVENT
}

function hasCopyInProgress() {
  return copyingPrizeId.value !== null
}

function isCopying(prizeId: number) {
  return copyingPrizeId.value === prizeId
}

function isOperationDisabled() {
  return hasCopyInProgress()
}

function isCopyButtonDisabled(prizeId: number) {
  return props.canEditCampaign === false || !isBeforeEventCampaign() || hasCopyInProgress() || isCopying(prizeId)
}

function handleChangePagingNo(page: number) {
  if (isOperationDisabled()) {
    return
  }
  props.onChangePagingNo(page)
}

function handleChangeLimitCount(limitCount: number) {
  if (isOperationDisabled()) {
    return
  }
  props.onChangeLimitCount(limitCount)
}

async function copyPrize(prizeId: number) {
  if (isCopyButtonDisabled(prizeId)) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|複製')
  copyingPrizeId.value = prizeId

  try {
    const response = await postInstagramInstantwinPrizeCopy(prizeId)
    const errorType = 'error' in response ? response.error.type : null
    if (errorType || !response.data) {
      let message = '応募グループの複製に失敗しました。'
      if (errorType === 'PERMISSION_DENIED') {
        message = '応募グループを複製する権限がありません。'
      } else if (errorType === 'NOT_EXISTS') {
        message = '複製対象の応募グループが存在しません。'
      } else if (errorType === 'CAMPAIGN_STATUS_INVALID') {
        message = '開始前のみ実行可能です（キャンペーン中/終了後は複製できません）。'
      }

      proxy.$notify({
        title: message,
        customClass: 'danger',
        duration: 5000
      })
      return
    }

    proxy.$notify({
      title: '複製が完了しました。',
      customClass: 'success',
      duration: 5000
    })
    await props.onRefresh()
  } catch {
    proxy.$notify({
      title: '応募グループの複製に失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  } finally {
    copyingPrizeId.value = null
  }
}

/**
 * シナリオエディタを閉じる
 */
function closeScenarioEditor() {
  isScenarioEditorVisible.value = false
  editingScenarioPrizeId.value = null
}

/**
 * シナリオ保存成功時の処理
 */
function onScenarioSaved() {
  closeScenarioEditor()
}

function closePretestDialog() {
  isPretestDialogVisible.value = false
  activePretestPrizeId.value = null
}

function openPreviewDialog(prizeId: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|プレビュー')
  previewingPrizeId.value = prizeId
  isPreviewDialogVisible.value = true
}

function closePreviewDialog() {
  isPreviewDialogVisible.value = false
  previewingPrizeId.value = null
}

expose({
  openCreateDialog
})

return { __sfc: true,props, proxy, isEditDialogVisible, editingPrizeId, isScenarioEditorVisible, editingScenarioPrizeId, isPretestDialogVisible, activePretestPrizeId, isPreviewDialogVisible, previewingPrizeId, copyingPrizeId, headerClick, renderHeader, getKeywordText, openCampaignApplicant, openEditDialog, openCreateDialog, closeEditDialog, onSaved, openScenarioEditor, openPretestDialog, isBeforeEventCampaign, hasCopyInProgress, isCopying, isOperationDisabled, isCopyButtonDisabled, handleChangePagingNo, handleChangeLimitCount, copyPrize, closeScenarioEditor, onScenarioSaved, closePretestDialog, openPreviewDialog, closePreviewDialog, Pagination, formatNumber, InstagramDmTestDialog, InstagramInstantwinPrizeEditDialog, InstagramScenarioEditorDialog, InstagramScenarioPreviewDialog }
}

})